import React, { useEffect, useMemo, useState } from 'react';
import ProductCard from '../components/ProductCard';
import axiosInstance from '../config/axios';

const categoryFilters = [
  'All',
  'Tech',
  'Home',
  'Style',
  'Collectibles',
  'Everyday',
];

function inferCategory(product) {
  const text = `${product.name} ${product.description}`.toLowerCase();
  if (/(phone|laptop|computer|tablet|camera|headphone|keyboard|monitor|tech|console)/.test(text)) return 'Tech';
  if (/(chair|table|desk|sofa|lamp|kitchen|home|decor|appliance)/.test(text)) return 'Home';
  if (/(shirt|shoe|jacket|watch|bag|clothing|fashion|style)/.test(text)) return 'Style';
  if (/(card|collect|vintage|rare|book|art|figure)/.test(text)) return 'Collectibles';
  return 'Everyday';
}

function HomePage() {
  const [products, setProducts] = useState([]);
  const [search, setSearch] = useState('');
  const [sortOption, setSortOption] = useState('Most Recent');
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const { data } = await axiosInstance.get('/api/products');
        setProducts(data);
      } catch (err) {
        setError('Could not load products right now. Please try again in a moment.');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase();

    return [...products]
      .filter((product) => {
        const matchesSearch =
          product.name.toLowerCase().includes(normalizedSearch) ||
          product.description.toLowerCase().includes(normalizedSearch) ||
          product.addedBy?.username?.toLowerCase().includes(normalizedSearch);
        const matchesCategory = category === 'All' || inferCategory(product) === category;
        return matchesSearch && matchesCategory;
      })
      .sort((a, b) => {
        if (sortOption === 'Price Low to High') return a.price - b.price;
        if (sortOption === 'Price High to Low') return b.price - a.price;
        if (sortOption === 'In Stock First') return Number(b.quantity > 0) - Number(a.quantity > 0);
        return new Date(b.createdAt) - new Date(a.createdAt);
      });
  }, [category, products, search, sortOption]);

  const activeCount = products.filter((product) => product.quantity > 0).length;

  return (
    <div className="page-shell">
      <section className="shop-hero">
        <div className="shop-hero-copy">
          <span className="eyebrow">Fresh finds from local sellers</span>
          <h1>Shop useful deals, list your own, and keep everything in one cart.</h1>
          <p>
            Browse real listings, compare prices fast, and buy from people already using the marketplace.
          </p>
        </div>
        <div className="hero-panel">
          <div>
            <span className="metric-value">{products.length}</span>
            <span className="metric-label">total listings</span>
          </div>
          <div>
            <span className="metric-value">{activeCount}</span>
            <span className="metric-label">ready to buy</span>
          </div>
          <div>
            <span className="metric-value">5</span>
            <span className="metric-label">photos per item</span>
          </div>
        </div>
      </section>

      <section className="market-toolbar" aria-label="Product controls">
        <div className="search-box">
          <label htmlFor="product-search">Search marketplace</label>
          <input
            id="product-search"
            type="search"
            placeholder="Search products, descriptions, or sellers"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="select-box">
          <label htmlFor="sort-products">Sort by</label>
          <select id="sort-products" value={sortOption} onChange={(e) => setSortOption(e.target.value)}>
            <option>Most Recent</option>
            <option>In Stock First</option>
            <option>Price Low to High</option>
            <option>Price High to Low</option>
          </select>
        </div>
      </section>

      <div className="category-row" aria-label="Categories">
        {categoryFilters.map((filter) => (
          <button
            key={filter}
            className={`category-pill ${category === filter ? 'active' : ''}`}
            type="button"
            onClick={() => setCategory(filter)}
          >
            {filter}
          </button>
        ))}
      </div>

      {error && <div className="notice error">{error}</div>}
      {loading ? (
        <div className="state-card">Loading the latest listings...</div>
      ) : filteredProducts.length === 0 ? (
        <div className="state-card">
          <h2>No matching items</h2>
          <p>Try a broader search or clear the category filter.</p>
        </div>
      ) : (
        <section className="product-grid" aria-label="Products">
          {filteredProducts.map((product) => (
            <ProductCard key={product._id} product={product} />
          ))}
        </section>
      )}
    </div>
  );
}

export default HomePage;
